import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { TABLET_HEADER_SEARCH_MEDIA } from './layoutConstants';

type HeaderSearchSlotContextValue = {
  slot: HTMLDivElement | null;
  setSlot: Dispatch<SetStateAction<HTMLDivElement | null>>;
  /** A page has portaled its own search into the slot (catalog, collection, print). */
  claimed: boolean;
  /** Register that claim. Call the returned function to release it. */
  claim: () => () => void;
};

const HeaderSearchSlotContext = createContext<HeaderSearchSlotContextValue | null>(null);

export function HeaderSearchSlotProvider({
  slot,
  setSlot,
  children,
}: {
  slot: HTMLDivElement | null;
  setSlot: Dispatch<SetStateAction<HTMLDivElement | null>>;
  children: ReactNode;
}) {
  const [claimedCount, setClaimedCount] = useState(0);
  const claim = useCallback(() => {
    setClaimedCount((count) => count + 1);
    return () => setClaimedCount((count) => Math.max(0, count - 1));
  }, []);
  const value = useMemo(
    () => ({ slot, setSlot, claimed: claimedCount > 0, claim }),
    [slot, setSlot, claimedCount, claim],
  );
  return (
    <HeaderSearchSlotContext.Provider value={value}>
      {children}
    </HeaderSearchSlotContext.Provider>
  );
}

export function useHeaderSearchSlot(): HTMLDivElement | null {
  return useContext(HeaderSearchSlotContext)?.slot ?? null;
}

export function useSetHeaderSearchSlot(): Dispatch<SetStateAction<HTMLDivElement | null>> {
  return useContext(HeaderSearchSlotContext)?.setSlot ?? (() => {});
}

export function useHeaderSearchClaimed(): boolean {
  return useContext(HeaderSearchSlotContext)?.claimed ?? false;
}

/** Hide the global header search while this page renders its own into the slot. */
export function useClaimTabletHeaderSearch(active: boolean): void {
  const claim = useContext(HeaderSearchSlotContext)?.claim;
  useLayoutEffect(() => {
    if (!active || !claim) return undefined;
    return claim();
  }, [active, claim]);
}

/** True while the catalog search belongs in the header instead of the filter column. */
export function useTabletHeaderSearch(): boolean {
  const [matches, setMatches] = useState(false);

  useLayoutEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(TABLET_HEADER_SEARCH_MEDIA);
    const sync = () => setMatches(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return matches;
}
